import React, { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import CookieConsent from "@/components/CookieConsent";
import AIChatWidget from "@/components/AIChatWidget";
import { enableAutoPageviews } from "@/lib/analytics";
import ScrollToTop from "./components/ScrollToTop";
import LazyLoadErrorBoundary from "./components/LazyLoadErrorBoundary";
import { LanguageProvider } from "@/contexts/LanguageContext";

// Eager load only critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PasswordProtection from "./pages/PasswordProtection";

// Lazy load all other pages for better performance
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Auth = lazy(() => import("./pages/Auth"));
const Modul0 = lazy(() => import("./pages/Modul0"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Programm = lazy(() => import("./pages/Programm"));
const WarumMaip = lazy(() => import("./pages/WarumMaip"));
const Experten = lazy(() => import("./pages/Experten"));
const Wissen = lazy(() => import("./pages/Wissen"));
const ArtikelDetail = lazy(() => import("./pages/ArtikelDetail"));
const Gestaltungsrat = lazy(() => import("./pages/Gestaltungsrat"));
const IdeeenBoard = lazy(() => import("./pages/IdeeenBoard"));
const UnifiedAdmin = lazy(() => import("./pages/UnifiedAdmin"));
const AdminAnfragen = lazy(() => import("./pages/AdminAnfragen"));
const AdminExperten = lazy(() => import("./pages/AdminExperten"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const CMSExperten = lazy(() => import("./pages/CMSExperten"));
const ExpertenBereich = lazy(() => import("./pages/ExpertenBereich"));
const ExpertenForumPage = lazy(() => import("./pages/ExpertenForumPage"));
const ExpertenRessourcenPage = lazy(() => import("./pages/ExpertenRessourcenPage"));
const ExpertenWebinarePage = lazy(() => import("./pages/ExpertenWebinarePage"));
const ForumThread = lazy(() => import("./pages/ForumThread"));
const GlossarDownload = lazy(() => import("./pages/downloads/Glossar"));
const Glossar = lazy(() => import("./pages/Glossar"));
const Checkliste = lazy(() => import("./pages/downloads/Checkliste"));
const AdminContracts = lazy(() => import("./pages/admin/AdminContracts"));
const Arbeitsblatt = lazy(() => import("./pages/downloads/Arbeitsblatt"));
const Preise = lazy(() => import("./pages/Preise"));
const Foerdermoeglichkeiten = lazy(() => import("./pages/Foerdermoeglichkeiten"));
const PotenzialAnalyse = lazy(() => import("./pages/PotenzialAnalyse"));
const Grundlagen = lazy(() => import("./pages/Grundlagen"));
const GrundlagenLektion1 = lazy(() => import("./pages/GrundlagenLektion1"));
const GrundlagenLektion2 = lazy(() => import("./pages/GrundlagenLektion2"));
const GrundlagenLektion3 = lazy(() => import("./pages/GrundlagenLektion3"));
const GrundlagenLektion4 = lazy(() => import("./pages/GrundlagenLektion4"));
const GrundlagenLektion5 = lazy(() => import("./pages/GrundlagenLektion5"));
const DatenschutzKompass = lazy(() => import("./pages/DatenschutzKompass"));
const DatenschutzKompassInfo = lazy(() => import("./pages/DatenschutzKompassInfo"));
const DatenschutzLektion0 = lazy(() => import("./pages/DatenschutzLektion0"));
const DatenschutzLektion1 = lazy(() => import("./pages/DatenschutzLektion1"));
const DatenschutzLektion2 = lazy(() => import("./pages/DatenschutzLektion2"));
const DatenschutzLektion3 = lazy(() => import("./pages/DatenschutzLektion3"));
const DatenschutzLektion4 = lazy(() => import("./pages/DatenschutzLektion4"));
const DatenschutzLektion5 = lazy(() => import("./pages/DatenschutzLektion5"));
const DatenschutzLektion6 = lazy(() => import("./pages/DatenschutzLektion6"));
const DatenschutzLektion7 = lazy(() => import("./pages/DatenschutzLektion7"));
const DatenschutzLektion8 = lazy(() => import("./pages/DatenschutzLektion8"));
const DatenschutzLektion9 = lazy(() => import("./pages/DatenschutzLektion9"));
const DatenschutzLektion10 = lazy(() => import("./pages/DatenschutzLektion10"));
const DatenschutzRessourcen = lazy(() => import("./pages/DatenschutzRessourcen"));
const Mitbestimmung = lazy(() => import("./pages/Mitbestimmung"));
const MitbestimmungInfo = lazy(() => import("./pages/MitbestimmungInfo"));
const MitbestimmungLektion1 = lazy(() => import("./pages/MitbestimmungLektion1"));
const MitbestimmungLektion2 = lazy(() => import("./pages/MitbestimmungLektion2"));
const MitbestimmungLektion3 = lazy(() => import("./pages/MitbestimmungLektion3"));
const MitbestimmungLektion4 = lazy(() => import("./pages/MitbestimmungLektion4"));
const MitbestimmungLektion5 = lazy(() => import("./pages/MitbestimmungLektion5"));
const MitbestimmungLektion6 = lazy(() => import("./pages/MitbestimmungLektion6"));
const MitbestimmungRessourcen = lazy(() => import("./pages/MitbestimmungRessourcen"));
const BuchungVerwalten = lazy(() => import("./pages/BuchungVerwalten"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const Voraussetzungen = lazy(() => import("./pages/Voraussetzungen"));
const Nutzungsbedingungen = lazy(() => import("./pages/Nutzungsbedingungen"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const FeatureMatrix = lazy(() => import("./pages/FeatureMatrix"));
const KIPilot = lazy(() => import("./pages/KIPilot"));
const KILotse = lazy(() => import("./pages/KILotse"));
const KIOekonom = lazy(() => import("./pages/KIOekonom"));
const AkzeptanzManager = lazy(() => import("./pages/AkzeptanzManager"));
const RolloutFahrplan = lazy(() => import("./pages/RolloutFahrplan"));
const LotsenLanding = lazy(() => import("./pages/LotsenLanding"));
const InhouseProgramm = lazy(() => import("./pages/InhouseProgramm"));
// MaipAdmin removed – replaced by UnifiedAdmin
const AuditDashboard = lazy(() => import("./pages/AuditDashboard"));
const FacilityDashboard = lazy(() => import("./pages/FacilityDashboard"));
const MasterAuditDashboard = lazy(() => import("./pages/MasterAuditDashboard"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const UserCockpit = lazy(() => import("./pages/UserCockpit"));
const TechnicalWhitepaper = lazy(() => import("./pages/TechnicalWhitepaper"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));
const AIPrompting = lazy(() => import("./pages/AIPrompting"));
const QMRisikomanagement = lazy(() => import("./pages/QMRisikomanagement"));
const PreFlight = lazy(() => import("./pages/PreFlight"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const WissenschaftMethodik = lazy(() => import("./pages/WissenschaftMethodik"));
const ContentAudit = lazy(() => import("./pages/admin/ContentAudit"));

// Lösungs-Seiten (Zielgruppen)
const LoesungenKliniken = lazy(() => import("./pages/LoesungenKliniken"));
const LoesungenPflegeheime = lazy(() => import("./pages/LoesungenPflegeheime"));
const LoesungenPflegedienste = lazy(() => import("./pages/LoesungenPflegedienste"));
const CulturalOnboarding = lazy(() => import("./pages/CulturalOnboarding"));
const CulturalOnboardingModule = lazy(() => import("./pages/CulturalOnboardingModule"));
const Bestellen = lazy(() => import("./pages/Bestellen"));
const Seminare = lazy(() => import("./pages/Seminare"));
const Sicherheit = lazy(() => import("./pages/Sicherheit"));
const ArtikelChangeManagement = lazy(() => import("./pages/ArtikelChangeManagement"));
const ArtikelEuAiAct = lazy(() => import("./pages/ArtikelEuAiAct"));
const KursModulAFuehrungskraefte = lazy(() => import("./pages/KursModulAFuehrungskraefte"));
const KursModulBDatenschutz = lazy(() => import("./pages/KursModulBDatenschutz"));
const DownloadProblemFinder = lazy(() => import("./pages/DownloadProblemFinder"));
const Plattform = lazy(() => import("./pages/Plattform"));
const Netzwerk = lazy(() => import("./pages/Netzwerk"));
const LeadershipPreBoarding = lazy(() => import("./pages/LeadershipPreBoarding"));
const LeadershipHub = lazy(() => import("./pages/LeadershipHub"));
const SurvivalKit = lazy(() => import("./pages/SurvivalKit"));
const DigitalWorkbook = lazy(() => import("./pages/DigitalWorkbook"));
const LeadershipGuard = lazy(() => import("./components/LeadershipGuard"));
const SeminarDetail = lazy(() => import("./pages/SeminarDetail"));

// QueryClient with optimized caching strategies
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh longer
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on every window focus
      retry: 1, // Only retry failed requests once
    },
  },
});

// Routes, die vom Passwortschutz ausgenommen sind (eigener Lizenz-Code-Schutz)
const PUBLIC_ROUTES = ['/ki-pilot', '/ki-lotse', '/ki-oekonom', '/akzeptanz-manager', '/rollout-fahrplan', '/coach-dashboard', '/verify', '/ai-prompting', '/qm-risikomanagement', '/pre-flight', '/dashboard', '/cultural-onboarding', '/auth', '/leadership-hub', '/survival-kit', '/digital-workbook'];

const AppGate = ({ hasAccess }: { hasAccess: boolean | null }) => {
  const { pathname } = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.some((route) => pathname.startsWith(route));

  if (hasAccess === null && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Wird geladen...</div>
      </div>
    );
  }

  if (!hasAccess && !isPublicRoute) {
    return <PasswordProtection />;
  }

  return (
    <>
      <CookieConsent />
      <ScrollToTop />
      <main id="main-content">
        <LazyLoadErrorBoundary>
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center">Lädt...</div>
            }
          >
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGB />} />
              <Route path="/buchung-verwalten" element={<BuchungVerwalten />} />
              <Route path="/widerruf" element={<Widerruf />} />
              <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
              <Route path="/voraussetzungen" element={<Voraussetzungen />} />
              <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/modul-0" element={<Modul0 />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/programm" element={<Programm />} />
              <Route path="/warum-maip" element={<WarumMaip />} />
              <Route path="/experten" element={<Experten />} />
              <Route path="/dozierende-team" element={<Experten />} />
              <Route path="/wissenschaft-methodik" element={<WissenschaftMethodik />} />
              <Route path="/wissen" element={<Wissen />} />
              <Route path="/wissen/gestaltungsrat" element={<Gestaltungsrat />} />
              <Route path="/gestaltungsrat" element={<Gestaltungsrat />} />
              <Route path="/wissen/ideen-board" element={<IdeeenBoard />} />
              <Route path="/ideenboard" element={<IdeeenBoard />} />
              <Route path="/artikel/:slug" element={<ArtikelDetail />} />
              <Route path="/experten-bereich" element={<ExpertenBereich />} />
              <Route path="/experten-bereich/forum" element={<ExpertenForumPage />} />
              <Route path="/experten-bereich/ressourcen" element={<ExpertenRessourcenPage />} />
              <Route path="/experten-bereich/webinare" element={<ExpertenWebinarePage />} />
              <Route path="/cms/experten" element={<CMSExperten />} />
              <Route path="/experten/forum/:threadId" element={<ForumThread />} />
              <Route path="/admin" element={<UnifiedAdmin />} />
              <Route path="/admin/anfragen" element={<AdminAnfragen />} />
              <Route path="/admin/experten" element={<AdminExperten />} />
              <Route path="/admin/content" element={<UnifiedAdmin />} />
              <Route path="/glossar" element={<Glossar />} />
              <Route path="/downloads/glossar" element={<GlossarDownload />} />
              <Route path="/downloads/checkliste" element={<Checkliste />} />
              <Route path="/downloads/arbeitsblatt" element={<Arbeitsblatt />} />
              <Route path="/preise" element={<Preise />} />
              <Route path="/bestellen" element={<Bestellen />} />
              <Route path="/seminare" element={<Seminare />} />
              <Route path="/seminare/:id" element={<SeminarDetail />} />
              <Route path="/foerdermoeglichkeiten" element={<Foerdermoeglichkeiten />} />
              <Route path="/potenzial-analyse" element={<PotenzialAnalyse />} />
              <Route path="/grundlagen" element={<Grundlagen />} />
              <Route path="/grundlagen/lektion-1" element={<GrundlagenLektion1 />} />
              <Route path="/grundlagen/lektion-2" element={<GrundlagenLektion2 />} />
              <Route path="/grundlagen/lektion-3" element={<GrundlagenLektion3 />} />
              <Route path="/grundlagen/lektion-4" element={<GrundlagenLektion4 />} />
              <Route path="/grundlagen/lektion-5" element={<GrundlagenLektion5 />} />
              <Route path="/datenschutz-kompass" element={<DatenschutzKompass />} />
              <Route path="/datenschutz-kompass-info" element={<DatenschutzKompassInfo />} />
              <Route path="/datenschutz-kompass/lektion-0" element={<DatenschutzLektion0 />} />
              <Route path="/datenschutz-kompass/lektion-1" element={<DatenschutzLektion1 />} />
              <Route path="/datenschutz-kompass/lektion-2" element={<DatenschutzLektion2 />} />
              <Route path="/datenschutz-kompass/lektion-3" element={<DatenschutzLektion3 />} />
              <Route path="/datenschutz-kompass/lektion-4" element={<DatenschutzLektion4 />} />
              <Route path="/datenschutz-kompass/lektion-5" element={<DatenschutzLektion5 />} />
              <Route path="/datenschutz-kompass/lektion-6" element={<DatenschutzLektion6 />} />
              <Route path="/datenschutz-kompass/lektion-7" element={<DatenschutzLektion7 />} />
              <Route path="/datenschutz-kompass/lektion-8" element={<DatenschutzLektion8 />} />
              <Route path="/datenschutz-kompass/lektion-9" element={<DatenschutzLektion9 />} />
              <Route path="/datenschutz-kompass/lektion-10" element={<DatenschutzLektion10 />} />
              <Route path="/datenschutz-ressourcen" element={<DatenschutzRessourcen />} />
              <Route path="/mitbestimmung" element={<Mitbestimmung />} />
              <Route path="/mitbestimmung-info" element={<MitbestimmungInfo />} />
              <Route path="/mitbestimmung/lektion-1" element={<MitbestimmungLektion1 />} />
              <Route path="/mitbestimmung/lektion-2" element={<MitbestimmungLektion2 />} />
              <Route path="/mitbestimmung/lektion-3" element={<MitbestimmungLektion3 />} />
              <Route path="/mitbestimmung/lektion-4" element={<MitbestimmungLektion4 />} />
              <Route path="/mitbestimmung/lektion-5" element={<MitbestimmungLektion5 />} />
              <Route path="/mitbestimmung/lektion-6" element={<MitbestimmungLektion6 />} />
              <Route path="/mitbestimmung-ressourcen" element={<MitbestimmungRessourcen />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/checkout-success" element={<CheckoutSuccess />} />
              <Route path="/checkout-cancel" element={<CheckoutCancel />} />
              <Route path="/feature-matrix" element={<FeatureMatrix />} />
              <Route path="/ki-pilot" element={<KIPilot />} />
              <Route path="/ki-lotse" element={<KILotse />} />
              <Route path="/ki-oekonom" element={<KIOekonom />} />
              <Route path="/akzeptanz-manager" element={<AkzeptanzManager />} />
              <Route path="/rollout-fahrplan" element={<RolloutFahrplan />} />
              <Route path="/lotsen" element={<LotsenLanding />} />
              <Route path="/inhouse" element={<InhouseProgramm />} />
              <Route path="/maip-admin" element={<UnifiedAdmin />} />
              <Route path="/audit-dashboard" element={<AuditDashboard />} />
              <Route path="/audit-dashboard/:facilityId" element={<AuditDashboard />} />
              <Route path="/master-audit" element={<MasterAuditDashboard />} />
              <Route path="/master-audit/:organizationId" element={<MasterAuditDashboard />} />
              <Route path="/verify" element={<VerifyCertificate />} />
              <Route path="/verify/:id" element={<VerifyCertificate />} />
              <Route path="/cockpit" element={<UserCockpit />} />
              <Route path="/facility" element={<FacilityDashboard />} />
              <Route path="/whitepaper" element={<TechnicalWhitepaper />} />
              <Route path="/coach-dashboard" element={<CoachDashboard />} />
              <Route path="/ai-prompting" element={<AIPrompting />} />
              <Route path="/qm-risikomanagement" element={<QMRisikomanagement />} />
              <Route path="/pre-flight" element={<PreFlight />} />
              <Route path="/dashboard" element={<StudentDashboard />} />
              <Route path="/admin/content-audit" element={<ContentAudit />} />
              <Route path="/admin/contracts" element={<AdminContracts />} />
              <Route path="/loesungen/kliniken" element={<LoesungenKliniken />} />
              <Route path="/loesungen/pflegeheime" element={<LoesungenPflegeheime />} />
              <Route path="/loesungen/pflegedienste" element={<LoesungenPflegedienste />} />
              <Route path="/cultural-onboarding" element={<CulturalOnboarding />} />
              <Route path="/cultural-onboarding/:modulId" element={<CulturalOnboardingModule />} />
              <Route path="/sicherheit" element={<Sicherheit />} />
              <Route path="/wissen/artikel/change-management" element={<ArtikelChangeManagement />} />
              <Route path="/wissen/artikel/eu-ai-act" element={<ArtikelEuAiAct />} />
              <Route path="/kurse/modul-a-fuehrungskraefte" element={<KursModulAFuehrungskraefte />} />
              <Route path="/kurse/modul-b-datenschutz" element={<KursModulBDatenschutz />} />
              <Route path="/downloads/problem-finder.pdf" element={<DownloadProblemFinder />} />
              <Route path="/plattform" element={<Plattform />} />
              <Route path="/netzwerk" element={<Netzwerk />} />
              <Route path="/leadership-preboarding" element={<LeadershipPreBoarding />} />
              <Route path="/leadership-hub" element={<LeadershipGuard><LeadershipHub /></LeadershipGuard>} />
              <Route path="/survival-kit" element={<LeadershipGuard><SurvivalKit /></LeadershipGuard>} />
              <Route path="/digital-workbook" element={<LeadershipGuard><DigitalWorkbook /></LeadershipGuard>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </LazyLoadErrorBoundary>
      </main>
      <AIChatWidget />
    </>
  );
};

const App = () => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    // Initialize Plausible Analytics with automatic pageview tracking
    enableAutoPageviews();
    
    // Prüfe ob Zugang gewährt wurde
    const accessGranted = localStorage.getItem("siteAccessGranted") === "true";
    setHasAccess(accessGranted);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <AppGate hasAccess={hasAccess} />
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;